import React from 'react';
import { fmtMoney, fmtDate } from '../lib/utils';
import PrintHeader, { TitlePill, DEFAULT_COMPANY } from './PrintHeader';

const BLUE = '#1E3A8A';

export default function InvoicePrint({ invoice, company }) {
  const c = { ...DEFAULT_COMPANY, ...(company || {}) };
  const wholesale = invoice.type === 'wholesale';
  return (
    <div className="bg-white text-slate-900 font-sans text-base p-4" style={{ minWidth: 640 }}>
      <PrintHeader company={c} />
      <TitlePill
        en={wholesale ? 'Tax Invoice' : 'Cash Receipt'}
        ar={wholesale ? 'فاتورة' : 'إيصال نقدي'}
        number={invoice.invoiceNumber}
        date={fmtDate(invoice.date)}
      />

      <div className="flex justify-between items-end mb-5" style={{ color: BLUE }}>
        <div className="text-[16px]">
          <span className="font-semibold text-sm">Received From Mr./Mrs <span className="font-arabic" dir="rtl" style={{ unicodeBidi: 'isolate' }}>/ وصلنا من السيد / السادة</span>:</span>{' '}
          <span className="font-bold text-slate-900 border-b border-dotted border-slate-400 px-2 text-[18px]">{invoice.customerName || 'Walk-in Customer'}</span>
          {invoice.customerMobile && <span className="text-sm text-slate-500 ml-2 num" dir="ltr">{invoice.customerMobile}</span>}
        </div>
        <div className="text-sm">
          <span className="font-semibold">Status:</span> <span className="capitalize font-bold">{invoice.status}</span>
        </div>
      </div>

      <table className="w-full mb-6 border" style={{ borderColor: BLUE }}>
        <thead>
          <tr className="text-white text-left" style={{ backgroundColor: BLUE }}>
            <th className="px-3 py-2.5 text-[15px] font-semibold w-10">#</th>
            <th className="px-3 py-2.5 text-[15px] font-semibold">Description / <span className="font-arabic">البيان</span></th>
            <th className="px-3 py-2.5 text-[15px] font-semibold w-16">Size</th>
            <th className="px-3 py-2.5 text-[15px] font-semibold text-right w-16">Qty</th>
            <th className="px-3 py-2.5 text-[15px] font-semibold text-right w-28">Rate</th>
            <th className="px-3 py-2.5 text-[15px] font-semibold text-right w-32">Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoice.items.map((it, i) => (
            <tr key={i} className={i % 2 ? 'bg-[#FDF4F6]' : ''} style={{ borderTop: '1px solid #dbe3f5' }}>
              <td className="px-3 py-2.5 text-sm">{i + 1}</td>
              <td className="px-3 py-2.5 text-[16px] font-medium">{it.productName}</td>
              <td className="px-3 py-2.5 text-sm">{it.size || '-'}</td>
              <td className="px-3 py-2.5 text-right num text-[16px]">{it.quantity}</td>
              <td className="px-3 py-2.5 text-right num text-[16px] whitespace-nowrap">{fmtMoney(it.price, c.currency)}</td>
              <td className="px-3 py-2.5 text-right num text-[16px] font-semibold whitespace-nowrap">{fmtMoney(it.total, c.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-end">
        <div className="w-96 space-y-2">
          <Line label="Subtotal" ar="المجموع" value={fmtMoney(invoice.subtotal, c.currency)} />
          {invoice.discount > 0 && <Line label="Discount" ar="الخصم" value={`- ${fmtMoney(invoice.discount, c.currency)}`} />}
          {invoice.shippingCharges > 0 && <Line label="Delivery" ar="التوصيل" value={fmtMoney(invoice.shippingCharges, c.currency)} />}
          <div className="border-t-2 my-1" style={{ borderColor: BLUE }} />
          <Line label="Total" ar="الإجمالي" value={fmtMoney(invoice.total, c.currency)} bold />
          <Line label="Paid" ar="المدفوع" value={fmtMoney(invoice.amountPaid, c.currency)} />
          {invoice.dueAmount > 0 && <Line label="Balance Due" ar="المتبقي" value={fmtMoney(invoice.dueAmount, c.currency)} bold accent />}
        </div>
      </div>

      <div className="flex justify-between items-end mt-14 text-sm" style={{ color: BLUE }}>
        <div className="text-center">
          <div className="border-t border-dotted border-slate-400 w-56 mb-1.5" />
          <p>Receiver's Signature · <span className="font-arabic text-base">توقيع المستلم</span></p>
        </div>
        <div className="text-center">
          <div className="border-t border-dotted border-slate-400 w-56 mb-1.5" />
          <p>For {c.name} · <span className="font-arabic text-base">التوقيع</span></p>
        </div>
      </div>

      <div className="mt-8 pt-4 border-t text-center text-sm font-medium" style={{ borderColor: '#dbe3f5', color: BLUE }}>
        Thank you for your business · <span className="font-arabic text-base">شكراً لتعاملكم معنا</span>
      </div>
    </div>
  );
}

function Line({ label, ar, value, bold, accent }) {
  return (
    <div className={`flex justify-between ${bold ? 'text-[18px]' : 'text-[16px]'}`}>
      <span className={bold ? 'font-bold text-slate-900' : 'text-slate-500'}>
        {label} · <span className="font-arabic" style={{ unicodeBidi: 'isolate' }}>{ar}</span>
      </span>
      <span className={`num ${bold ? 'font-bold' : ''} ${accent ? 'text-red-600' : ''}`}>{value}</span>
    </div>
  );
}
